"use client";

import { useRouter } from "next/navigation";
import type { KeyboardEvent, MouseEvent } from "react";
import { withCurrentReturnContext } from "@/lib/navigation/return-context";

const MIDDLE_MOUSE_BUTTON = 1;

export const RECORD_ROW_ATTRIBUTE = "data-record-row";

interface RowOpenProps {
  [RECORD_ROW_ATTRIBUTE]: "" | undefined;
  role: "link" | "button" | undefined;
  "aria-expanded": boolean | undefined;
  tabIndex: number | undefined;
  onClick: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  onAuxClick: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  onMouseDown: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  onKeyDown: ((event: KeyboardEvent<HTMLElement>) => void) | undefined;
}

export interface RowOpenHandlers {
  isInteractive: boolean;
  props: RowOpenProps;
}

const INERT_PROPS: RowOpenProps = {
  [RECORD_ROW_ATTRIBUTE]: undefined,
  role: undefined,
  "aria-expanded": undefined,
  tabIndex: undefined,
  onClick: undefined,
  onAuxClick: undefined,
  onMouseDown: undefined,
  onKeyDown: undefined,
};

export function openRecordInNewTab(href: string): void {
  window.open(
    withCurrentReturnContext(href),
    "_blank",
    "noopener,noreferrer",
  );
}

export function useRowOpen<TData>({
  row,
  href,
  onRowClick,
  expanded,
}: {
  row: TData;
  href?: string;
  onRowClick?: (row: TData) => void;
  expanded?: boolean;
}): RowOpenHandlers {
  const router = useRouter();
  const isInteractive = Boolean(href || onRowClick);
  if (!isInteractive) return { isInteractive, props: INERT_PROPS };

  const openRecord = () => {
    if (onRowClick) {
      onRowClick(row);
    } else if (href) {
      router.push(withCurrentReturnContext(href));
    }
  };

  const role = href && !onRowClick ? "link" : "button";
  return {
    isInteractive,
    props: {
      [RECORD_ROW_ATTRIBUTE]: "",
      role,
      "aria-expanded": role === "button" ? expanded : undefined,
      tabIndex: 0,
      onClick: (event) => openFromClick(event, href, openRecord),
      onAuxClick: (event) => openFromMiddleClick(event, href),
      onMouseDown: (event) => suppressMiddleClickScroll(event, href),
      onKeyDown: (event) => openFromKey(event, role, openRecord),
    },
  };
}

function openFromClick(
  event: MouseEvent<HTMLElement>,
  href: string | undefined,
  openRecord: () => void,
): void {
  if (isInteractiveTarget(event.target, event.currentTarget)) return;
  if (href && (event.metaKey || event.ctrlKey)) {
    event.preventDefault();
    openRecordInNewTab(href);
    return;
  }
  openRecord();
}

function middleClickHref(
  event: MouseEvent<HTMLElement>,
  href: string | undefined,
): string | undefined {
  return event.button === MIDDLE_MOUSE_BUTTON &&
    !isInteractiveTarget(event.target, event.currentTarget)
    ? href
    : undefined;
}

function openFromMiddleClick(
  event: MouseEvent<HTMLElement>,
  href: string | undefined,
): void {
  const target = middleClickHref(event, href);
  if (target === undefined) return;
  event.preventDefault();
  openRecordInNewTab(target);
}

function suppressMiddleClickScroll(
  event: MouseEvent<HTMLElement>,
  href: string | undefined,
): void {
  if (middleClickHref(event, href) === undefined) return;
  event.preventDefault();
}

function openFromKey(
  event: KeyboardEvent<HTMLElement>,
  role: "link" | "button",
  openRecord: () => void,
): void {
  if (event.target !== event.currentTarget) return;
  const activatesButton = role === "button" && event.key === " ";
  if (event.key !== "Enter" && !activatesButton) return;
  if (activatesButton) event.preventDefault();
  openRecord();
}

export function isInteractiveTarget(
  target: EventTarget | null,
  container: HTMLElement,
): boolean {
  return ownedByDescendant(
    target,
    container,
    "a,button,input,label,select,textarea,[role='button']",
  );
}

export function consumesKey(
  target: EventTarget | null,
  container: HTMLElement,
  key: string,
): boolean {
  const editsText = ownedByDescendant(
    target,
    container,
    "input,select,textarea,[contenteditable='true']",
  );
  if (editsText) return true;
  if (ownedByDescendant(target, container, "button")) {
    return key === " " || key === "Enter";
  }
  return ownedByDescendant(target, container, "a") && key === "Enter";
}

export function ownsContextMenu(
  target: EventTarget | null,
  container: HTMLElement,
): boolean {
  return ownedByDescendant(
    target,
    container,
    "a[href],input,select,textarea,[contenteditable='true']",
  );
}

function ownedByDescendant(
  target: EventTarget | null,
  container: HTMLElement,
  selector: string,
): boolean {
  if (!(target instanceof Element)) return false;
  const owner = target.closest(selector);
  return owner !== null && owner !== container;
}
